'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';

const topics = ['AI & Machine Learning', 'Data Science', 'Health Informatics', 'Coding for Kids'];
const difficulties = ['Beginner', 'Intermediate', 'Advanced'];
const durations = ['Under 3 hours', '3–10 hours', '10–20 hours', '20+ hours'];
const ratings = ['4.5 & above', '4.0 & above', '3.5 & above'];

interface FilterSection {
  id: string;
  label: string;
  options: string[];
}

const filterSections: FilterSection[] = [
  { id: 'topic', label: 'Topic', options: topics },
  { id: 'difficulty', label: 'Difficulty', options: difficulties },
  { id: 'duration', label: 'Duration', options: durations },
  { id: 'rating', label: 'Rating', options: ratings },
];

export default function CatalogueFilters() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ topic: true, difficulty: true, duration: false, rating: false });
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const toggle = (section: string) => setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const toggleOption = (section: string, option: string) => {
    setSelected((prev) => {
      const current = prev[section] || [];
      return {
        ...prev,
        [section]: current.includes(option) ? current.filter((o) => o !== option) : [...current, option],
      };
    });
  };

  const totalSelected = Object.values(selected).flat().length;

  return (
    <aside className="hidden lg:block w-56 xl:w-64 flex-shrink-0">
      <div className="card p-4 sticky top-24">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-foreground">Filters</h3>
          {totalSelected > 0 && (
            <button
              onClick={() => setSelected({})}
              className="flex items-center gap-1 text-xs text-primary font-medium hover:underline"
            >
              <RotateCcw size={11} />
              Clear {totalSelected}
            </button>
          )}
        </div>
        <div className="space-y-4">
          {filterSections.map((section) => (
            <div key={`filter-${section.id}`} className="border-t border-border pt-4 first:border-t-0 first:pt-0">
              <button
                onClick={() => toggle(section.id)}
                className="flex items-center justify-between w-full text-sm font-semibold text-foreground mb-2 hover:text-primary transition-colors"
              >
                {section.label}
                {expanded[section.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {expanded[section.id] && (
                <div className="space-y-2 fade-in">
                  {section.options.map((option) => {
                    const isSelected = (selected[section.id] || []).includes(option);
                    return (
                      <label key={`filter-opt-${section.id}-${option}`} className="flex items-center gap-2.5 cursor-pointer group">
                        <div
                          onClick={() => toggleOption(section.id, option)}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 cursor-pointer ${
                            isSelected ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/60'
                          }`}
                        >
                          {isSelected && (
                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                              <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <span className={`text-xs transition-colors ${isSelected ? 'text-foreground font-medium' : 'text-muted-foreground group-hover:text-foreground'}`}>
                          {option}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}